import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import SignatureCanvas from "react-signature-canvas";
import { HiOutlineBars2 } from "react-icons/hi2";
import { PixelStyle } from "../../components/layout";
import { update, ref } from "firebase/database";
import { database, storage } from "../../config/config";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import moment from "moment";
import ExportToExcel from "../../components/exportToExcel";
import { SiGmail } from "react-icons/si";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { HeaderContainer } from "../../components/headerContainer";
import { RiDeleteBin6Line } from "react-icons/ri";
import { PDFDownloadLink } from "@react-pdf/renderer";
// import PDFDocument from "./pdf";
import { TimePicker } from "react-ios-time-picker";
import axios from "axios";
import { useAppContext } from "../../context/appContext";
import { API_URL } from "../../apis/apiurl";
import CardPopModel from "../../components/model/cardModel";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FileSpreadsheet, FileUp, FolderUp, LoaderCircle, Mail, SquarePen, Trash } from "lucide-react";
import { capitalize } from "@mui/material";
import { DeleteSecurityReportAPI } from "../../apis/index.js";
import EmailFormModal from "../../components/EmailFormModal";
import { MdOutlineDragIndicator } from "react-icons/md";
import Button from "../../components/ButtonX.js";
import { TopScreenLoader, ConfirmationModal, AlertModal } from "../../components";
import { getExcelDataByReportAPI } from "../../apis/excelData";
import AutocompleteInput from "../../components/AutocompleteInput";
import QRCode from "react-qr-code";

const normalizeTabularFormat = (format) => {
  if (!format || typeof format !== "string") return "";
  return format.toLowerCase().trim().replace(/\s+/g, " ");
};

const parseCheckpointFixedItem = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("{")) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return null;
      }
    }
    return { checkpointName: trimmed };
  }
  return null;
};

const parseCheckpointFixedAtRow = (column, bodyRowIndex) => {
  if (!column || !Array.isArray(column.fixed) || column.fixed[bodyRowIndex] == null) {
    return null;
  }
  return parseCheckpointFixedItem(column.fixed[bodyRowIndex]);
};

const getCheckpointNameFromFixed = (column, rowIndex) => {
  const parsed = parseCheckpointFixedAtRow(column, rowIndex);
  if (!parsed) return "";
  return parsed.checkpointName || parsed.CheckpointName || "";
};

const findTourColumn = (columns = []) =>
  columns.find(
    (col) =>
      (col?.type && String(col.type).toLowerCase() === "tour") ||
      normalizeTabularFormat(col?.format) === "tour"
  );

const getCheckpointNameForRow = (column, cell, rowIndex, columns = []) => {
  const fromTourObj =
    cell?.tour?.checkpointName ||
    cell?.tour?.CheckpointName ||
    cell?.checkpointName;
  if (fromTourObj) return fromTourObj;

  const fromColumnFixed = getCheckpointNameFromFixed(column, rowIndex);
  if (fromColumnFixed) return fromColumnFixed;

  const tourCol = findTourColumn(columns);
  const fromTourColFixed = getCheckpointNameFromFixed(tourCol, rowIndex);
  if (fromTourColFixed) return fromTourColFixed;

  const cellTitle = String(cell?.title || "").trim();
  const colTitle = String(column?.title || "").trim();
  if (cellTitle && cellTitle !== colTitle) return cellTitle;

  return cellTitle;
};

const formatFixedValueDisplay = (raw) => {
  const parsed = parseCheckpointFixedItem(raw);
  if (parsed?.checkpointName || parsed?.CheckpointName) {
    return parsed.checkpointName || parsed.CheckpointName;
  }
  if (typeof raw === "string" && raw.trim() && !raw.trim().startsWith("{")) {
    return raw.trim();
  }
  return "";
};

/** Match row cells to visual column even when API row order differs. */
const resolveTabularCellForColumn = (rowCells, column, bodyRowIndex) => {
  if (!Array.isArray(rowCells) || !column) {
    return { cell: null, rowIndex: -1 };
  }

  const colFmt = normalizeTabularFormat(column.format);
  const matches = [];
  rowCells.forEach((c, idx) => {
    if (normalizeTabularFormat(c?.format) === colFmt) {
      matches.push({ cell: c, idx });
    }
  });

  if (matches.length === 0) {
    if (column.originalIndex != null && rowCells[column.originalIndex] != null) {
      return { cell: rowCells[column.originalIndex], rowIndex: column.originalIndex };
    }
    return { cell: null, rowIndex: column.originalIndex ?? -1 };
  }

  if (matches.length === 1) {
    return { cell: matches[0].cell, rowIndex: matches[0].idx };
  }

  // When multiple same-format cells exist (e.g. many qrcode columns),
  // prefer the visual/original column index to avoid cross-column reuse.
  if (column.originalIndex != null && rowCells[column.originalIndex] != null) {
    const indexedCell = rowCells[column.originalIndex];
    if (normalizeTabularFormat(indexedCell?.format) === colFmt) {
      return { cell: indexedCell, rowIndex: column.originalIndex };
    }
  }

  if (colFmt === "qrcode" || colFmt === "tour") {
    const expectedColumnTitle = String(column?.title || "")
      .toLowerCase()
      .trim();
    if (expectedColumnTitle) {
      const titleHit = matches.find((m) => {
        const cellTitle = String(m?.cell?.title || "")
          .toLowerCase()
          .trim();
        return cellTitle === expectedColumnTitle;
      });
      if (titleHit) return { cell: titleHit.cell, rowIndex: titleHit.idx };
    }

    const fixedCheckpoint = parseCheckpointFixedAtRow(column, bodyRowIndex);
    const expectedName = String(
      fixedCheckpoint?.checkpointName || fixedCheckpoint?.CheckpointName || ""
    )
      .toLowerCase()
      .trim();
    if (expectedName) {
      const hit = matches.find((m) => {
        const cellName = String(
          m?.cell?.tour?.checkpointName ||
            m?.cell?.checkpointName ||
            m?.cell?.title ||
            ""
        )
          .toLowerCase()
          .trim();
        return cellName === expectedName;
      });
      if (hit) return { cell: hit.cell, rowIndex: hit.idx };
    }
  }

  return { cell: matches[0].cell, rowIndex: matches[0].idx };
};

// Sortable Row Component for drag and drop
const SortableRow = ({
  content,
  contentIndex,
  index,
  state,
  renderTableCell,
  DeleteTabularHandler,
  MomentFormatDate,
  sortedColumns, // New prop
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: content.id || `fallback-${index}-${contentIndex}`,
    disabled: !state.isEditable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Determine what to iterate: sorted columns or original row (fallback)
  const columnsToRender = sortedColumns || content?.row?.map((_, i) => ({ originalIndex: i }));

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`transition-shadow duration-200 ${
        isDragging
          ? "bg-blue-50 shadow-lg border-2 border-blue-200 z-50"
          : "hover:bg-gray-50"
      } ${!state.isEditable ? "opacity-80" : ""}`}
    >
      {columnsToRender.map((column, colIndex) => {
        const columnDef = sortedColumns?.[colIndex] || column;
        const columnFormat = normalizeTabularFormat(columnDef?.format);
        const useTourQrResolver =
          columnFormat === "tour" || columnFormat === "qrcode";

        let tableTabularData = content?.row?.[column.originalIndex];
        let tableTabularIndex = column.originalIndex;

        if (useTourQrResolver) {
          const { cell: resolvedCell, rowIndex: resolvedRowIndex } =
            resolveTabularCellForColumn(content?.row, columnDef, contentIndex);
          tableTabularData = resolvedCell;
          tableTabularIndex =
            resolvedRowIndex >= 0 ? resolvedRowIndex : column.originalIndex;
        }

        if (!tableTabularData && columnDef) {
          const tourColDef = findTourColumn(sortedColumns || []);
          const fixedCheckpoint = useTourQrResolver
            ? parseCheckpointFixedAtRow(columnDef, contentIndex) ||
              parseCheckpointFixedAtRow(tourColDef, contentIndex)
            : null;
          const fallbackQrValue = fixedCheckpoint?.qrcode || fixedCheckpoint?.location || "";
          const fallbackTitle = useTourQrResolver
            ? getCheckpointNameFromFixed(columnDef, contentIndex) ||
              getCheckpointNameFromFixed(tourColDef, contentIndex) ||
              fixedCheckpoint?.checkpointName ||
              fixedCheckpoint?.CheckpointName ||
              columnDef.title ||
              ""
            : columnDef.title || "";
          tableTabularData = {
            format: columnDef.format,
            title:
              columnDef.format === "fixed value"
                ? formatFixedValueDisplay(columnDef.fixed?.[contentIndex]) ||
                  columnDef.fixed?.[contentIndex] ||
                  ""
                : fallbackTitle,
            checkpointName: fixedCheckpoint?.checkpointName || "",
            tour:
              fixedCheckpoint && (fixedCheckpoint.qrcode || fixedCheckpoint.location)
                ? {
                    ...fixedCheckpoint,
                    qrcode: fixedCheckpoint.qrcode || fixedCheckpoint.location || "",
                    location: fixedCheckpoint.location || fixedCheckpoint.qrcode || "",
                  }
                : undefined,
            value:
              useTourQrResolver && normalizeTabularFormat(columnDef.format) === "qrcode"
                ? fallbackQrValue
                : columnDef.format === "Yes/No"
                  ? "false"
                  : "",
            time: Date.now().toString(),
          };
          tableTabularIndex = columnDef.originalIndex;
        }

        if (!tableTabularData || typeof tableTabularData !== "object")
          return <td key={colIndex} className="px-5 py-4" />;

        return (
        <td
          className="px-5 py-4 content-start"
          key={colIndex} // Use visual index as key
          style={{ minWidth: "150px" }}
        >
          {renderTableCell(
            tableTabularData,
            contentIndex,
            tableTabularIndex,
            index,
            listeners,
            attributes,
            columnDef
          )}
          {state?.reportData?.permission?.accessType === "viewLiveFullAccess" &&
          tableTabularData?.time ? (
            <div
              className={`text-xs text-zinc-400 mt-1 w-full ${
                normalizeTabularFormat(tableTabularData.format) === "tour"
                  ? "text-center pl-0"
                  : "text-left pl-1"
              } ${
                tableTabularData.format === "Photo Upload" ||
                tableTabularData.format === "photo capture" ||
                tableTabularData.format === "Photo Capture"
                  ? "ml-1"
                  : ""
              }`}
            >
              {MomentFormatDate(tableTabularData.time)}
            </div>
          ) : (
            <div>
              <div className="text-xs opacity-0 text-zinc-400 mt-1">
                no value
              </div>
            </div>
          )}
        </td>
      )})}
      <td className="content-start py-4">
        <div>
          <div className="flex justify-center">
            {content?.row[0]?.format === "fixed value" ? (
              ""
            ) : (
              <button
                disabled={!state.isEditable}
                onClick={() => DeleteTabularHandler(contentIndex, index)}
                className={`text-center text-sm ${
                  state.isEditable
                    ? "text-red-500 hover:text-red-700"
                    : "text-red-200"
                }`}
              >
                <Trash size={18} />
              </button>
            )}
          </div>
          <div>
            <div className="text-xs opacity-0 text-zinc-400 mt-1">
              no value
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

// E-signature cell component
const ESignatureCell = ({ value, isEditable, onChange, onSignatureSaved }) => {
  // Helper to trim whitespace from canvas
  function trimCanvas(copy) {
    const ctx = copy.getContext("2d");
    const width = copy.width;
    const height = copy.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    let top = null,
      left = null,
      right = null,
      bottom = null;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        if (imageData.data[idx + 3] > 0) {
          // alpha > 0
          if (top === null) top = y;
          if (left === null || x < left) left = x;
          if (right === null || x > right) right = x;
          bottom = y;
        }
      }
    }
    if (top === null) return copy; // blank canvas
    const trimmedWidth = right - left + 1;
    const trimmedHeight = bottom - top + 1;
    const trimmed = document.createElement("canvas");
    trimmed.width = trimmedWidth;
    trimmed.height = trimmedHeight;
    const trimmedCtx = trimmed.getContext("2d");
    trimmedCtx.putImageData(
      ctx.getImageData(left, top, trimmedWidth, trimmedHeight),
      0,
      0
    );
    return trimmed;
  }
  const [signature, setSignature] = React.useState(value || "");
  const sigPadRef = React.useRef();

  const handleClear = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
    }
    setSignature("");
    if (isEditable && onChange) {
      onChange("");
    }
  };

  const handleSave = () => {
    let imageData = "";
    if (sigPadRef.current) {
      let canvas = null;
      if (typeof sigPadRef.current.getCanvas === "function") {
        canvas = sigPadRef.current.getCanvas();
      } else if (typeof sigPadRef.current.getTrimmedCanvas === "function") {
        canvas = sigPadRef.current.getTrimmedCanvas();
      } else if (typeof sigPadRef.current.toDataURL === "function") {
        // fallback: try toDataURL directly
        imageData = sigPadRef.current.toDataURL("image/png");
      }
      if (canvas) {
        const trimmed = trimCanvas(canvas);
        imageData = trimmed.toDataURL("image/png");
      }
    }
    console.log("Signature image data:", imageData);
    setSignature(imageData);
    if (isEditable && onChange) {
      onChange(imageData);
    }
    if (!imageData) {
      alert(
        "Could not get signature image data. Please check the signature pad implementation."
      );
    } else {
      // Show popup when signature is successfully saved
      if (onSignatureSaved) {
        onSignatureSaved();
      }
    }
  };

  React.useEffect(() => {
    setSignature(value || "");
  }, [value]);

  return (
    <div className="flex flex-col items-center justify-center gap-2">
      {isEditable ? (
        <>
          <div
            style={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              width: "300px",
              height: "120px",
              background: "#fff",
            }}
          >
            <SignatureCanvas
              ref={sigPadRef}
              penColor="black"
              backgroundColor="white"
              canvasProps={{
                width: 300,
                height: 120,
                className: "signature-canvas",
              }}
            />
          </div>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className="px-3 py-1 bg-gray-200 rounded"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              type="button"
              className="px-3 py-1 bg-blue-500 text-white rounded"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </>
      ) : signature ? (
        <img
          src={signature}
          alt="Signature"
          style={{
            width: "300px",
            height: "120px",
            border: "1px solid #ccc",
            borderRadius: "8px",
          }}
        />
      ) : (
        <span className="text-gray-500">No signature</span>
      )}
    </div>
  );
};

// State reducer
const reducer = (state, action) => {
  switch (action.type) {
    case "SET_REPORT_DATA":
      return { ...state, ...action.payload };
    case "SET_MESSAGE":
      return { ...state, message: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "TOGGLE_SUBMIT":
      return { ...state, wantToSubmitToggle: action.payload };
    case "TOGGLE_EDITABLE":
      return { ...state, isEditable: action.payload };
    case "SET_TABLE_DATA":
      return {
        ...state,
        tableDataByIndex: {
          ...state.tableDataByIndex,
          [action.index]: action.payload,
        },
      };
    case "SET_ORIGINAL_TABLE_DATA":
      return {
        ...state,
        originalTableDataByIndex: {
          ...state.originalTableDataByIndex,
          [action.index]: action.payload,
        },
      };
    case "SET_UPLOADING":
      return {
        ...state,
        uploadingByIndex: {
          ...state.uploadingByIndex,
          [action.index]: action.payload,
        },
      };
    case "REORDER_ROWS":
      return {
        ...state,
        tableDataByIndex: {
          ...state.tableDataByIndex,
          [action.tableIndex]: action.payload,
        },
      };
    case "TOGGLE_SIGNATURE_POPUP":
      return { ...state, showSignaturePopup: action.payload };
    default:
      return state;
  }
};
const initialState = {
  reportData: {},
  reportName: "",
  tables: [],
  companyName: "",
  companyId: "",
  companyLocation: "",
  status: "",
  message: "",
  loading: false,
  wantToSubmitToggle: false,
  isEditable: false, // Single flag for all tables
  tableDataByIndex: {},
  originalTableDataByIndex: {},
  uploadingByIndex: {},
  showSignaturePopup: false,
};

const ViewManagementReport = () => {
  const { UserType } = useAppContext();
  const { managereportId: reportId } = useParams();
  const [state, dispatch] = useReducer(reducer, initialState);

  // Additional state for loaders and modals
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const [globalLoadingText, setGlobalLoadingText] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("info");

  // Excel data state for autocomplete suggestions
  const [excelDataByTable, setExcelDataByTable] = useState({});
  const [usedValuesByTable, setUsedValuesByTable] = useState({});

  // Helper functions for alerts
  const showAlert = (message, type = "info") => {
    setAlertMessage(message);
    setAlertType(type);
    setShowSuccessAlert(true);
  };

  const showError = (message) => {
    setAlertMessage(message);
    setShowErrorAlert(true);
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event, tableIndex) => {
    const { active, over } = event;

    console.log("Drag end event:", event);
    console.log("Table index:", tableIndex);

    if (active.id !== over?.id) {
      const tableData = state.tableDataByIndex[tableIndex] || [];
      const oldIndex = tableData.findIndex((row) => row.id === active.id);
      const newIndex = tableData.findIndex((row) => row.id === over.id);

      console.log("Moving from index", oldIndex, "to", newIndex);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(tableData, oldIndex, newIndex);

        console.log("New order:", newOrder);

        dispatch({
          type: "REORDER_ROWS",
          tableIndex: tableIndex,
          payload: newOrder,
        });
      }
    }
  };

  // Fetch report data
  const fetchReportData = async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    setIsGlobalLoading(true);
    // setGlobalLoadingText("Loading report data...");
    try {
      const response = await axios.post(
        `${API_URL}/graphql`,
        {
          query: `mutation FetchGeneratedReportById { fetchGeneratedReportById(id: ${reportId}) { success data } }`,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: UserType?.token,
          },
        }
      );

      const responseData = response.data?.data?.fetchGeneratedReportById;
      if (responseData?.success) {
        const data = responseData.data;
        // console.log("responseData.data",data)
        const newState = {
          reportData: data,
          reportName: data.template || "",
          tables: data.tables || [],
          companyName: data.companyName || "",
          companyId: data.companyId || "",
          companyLocation: data.companyLocation || "",
          status: data.status || "",
          message: "",
          email:data?.email,
        };
        const tableDataByIndex = {};
        const originalTableDataByIndex = {};
        (data?.filledData || []).forEach((item, index) => {
          const columns = data?.tables?.[index]?.columns || [];
          const tableWithIds = (item?.table || []).map((row, rowIndex) => {
            const existingRow = row?.row || [];
            const usedIndices = new Set();
            const normalizedRow = columns.map((col, colIndex) => {
              const tourColForRow = findTourColumn(columns);
              const findMatchingCell = () => {
                const colFormat = (col.format || "").toLowerCase();
                const expectedFixedTitle = col.format === "fixed value" ? col.fixed?.[rowIndex] : null;
                const expectedQrCheckpointName = (() => {
                  if (colFormat !== "qrcode") return "";
                  const name = getCheckpointNameFromFixed(col, rowIndex) ||
                    getCheckpointNameFromFixed(tourColForRow, rowIndex);
                  return name.toString().toLowerCase().trim();
                })();
                const expectedTourCheckpointName = (() => {
                  if (colFormat !== "tour") return "";
                  return getCheckpointNameFromFixed(col, rowIndex)
                    .toString()
                    .toLowerCase()
                    .trim();
                })();
                let fallbackSameFormat = null;
                for (let i = 0; i < existingRow.length; i++) {
                  if (usedIndices.has(i)) continue;
                  const cell = existingRow[i];
                  if (!cell || typeof cell !== "object") continue;
                  const cellFormat = (cell.format || "").toLowerCase();
                  if (cellFormat !== colFormat) continue;
                  if (!fallbackSameFormat) {
                    fallbackSameFormat = { cell, index: i };
                  }
                  if (col.format === "fixed value") {
                    const cellTitle = String(cell.title ?? "").trim();
                    const matchesExpected = expectedFixedTitle != null && cellTitle === String(expectedFixedTitle).trim();
                    const matchesAnyFixed = col.fixed && col.fixed.some((f) => String(f).trim() === cellTitle);
                    if (matchesExpected || matchesAnyFixed) {
                      usedIndices.add(i);
                      return { cell, index: i };
                    }
                  } else if (colFormat === "qrcode") {
                    const cellCheckpoint = (
                      cell?.tour?.checkpointName ||
                      cell?.checkpointName ||
                      cell?.title ||
                      ""
                    )
                      .toString()
                      .toLowerCase()
                      .trim();
                    if (
                      (expectedQrCheckpointName &&
                        cellCheckpoint === expectedQrCheckpointName) ||
                      !expectedQrCheckpointName
                    ) {
                      usedIndices.add(i);
                      return { cell, index: i };
                    }
                  } else if (colFormat === "tour") {
                    const cellCheckpoint = String(
                      cell?.tour?.checkpointName ||
                      cell?.checkpointName ||
                      cell?.title ||
                      ""
                    )
                      .toLowerCase()
                      .trim();
                    if (
                      (expectedTourCheckpointName &&
                        cellCheckpoint === expectedTourCheckpointName) ||
                      !expectedTourCheckpointName
                    ) {
                      usedIndices.add(i);
                      return { cell, index: i };
                    }
                  } else {
                    const cellTitle = (cell.title || "").toString().toLowerCase().trim();
                    const colTitle = (col.title || "").toString().toLowerCase().trim();
                    if (cellTitle === colTitle) {
                      usedIndices.add(i);
                      return { cell, index: i };
                    }
                  }
                }
                if (fallbackSameFormat) {
                  usedIndices.add(fallbackSameFormat.index);
                  return fallbackSameFormat;
                }
                return null;
              };
              const match = findMatchingCell();
              const existingCell = match ? match.cell : null;
              if (existingCell && typeof existingCell === "object") {
                const isNumCol = (col.format || existingCell.format || "").toLowerCase() === "number";
                const val = existingCell.value;
                if (isNumCol && val !== "" && val !== null && val !== undefined) {
                  const num = Number(String(val).replace(/[^0-9.-]/g, ""));
                  return { ...existingCell, value: isNaN(num) ? val : num };
                }
                if (normalizeTabularFormat(col.format) === "tour") {
                  const resolvedCheckpoint = getCheckpointNameForRow(
                    col,
                    existingCell,
                    rowIndex,
                    columns
                  );
                  if (resolvedCheckpoint) {
                    return {
                      ...existingCell,
                      title: resolvedCheckpoint,
                      checkpointName: resolvedCheckpoint,
                      tour: {
                        ...(existingCell.tour || {}),
                        checkpointName: resolvedCheckpoint,
                      },
                    };
                  }
                }
                return existingCell;
              }
              const colFormatNorm = normalizeTabularFormat(col.format);
              let defaultTitle = col.title || "";
              if (col.format === "fixed value") {
                defaultTitle =
                  formatFixedValueDisplay(col.fixed?.[rowIndex]) ||
                  col.fixed?.[rowIndex] ||
                  "";
              } else if (colFormatNorm === "tour") {
                defaultTitle =
                  getCheckpointNameFromFixed(col, rowIndex) ||
                  getCheckpointNameFromFixed(tourColForRow, rowIndex) ||
                  col.title ||
                  "";
              }
              return {
                format: col.format,
                title: defaultTitle,
                value: col.format === "Yes/No" ? "false" : "",
                time: Date.now().toString(),
              };
            });
            return {
              ...row,
              row: normalizedRow,
              id:
                row.id ||
                `row-${Date.now()}-${index}-${rowIndex}-${Math.random()
                  .toString(36)
                  .substr(2, 9)}`,
            };
          });
          tableDataByIndex[index] = tableWithIds;
          originalTableDataByIndex[index] = JSON.parse(
            JSON.stringify(tableWithIds)
          ); // Deep copy
        });
        dispatch({
          type: "SET_REPORT_DATA",
          payload: { ...newState, tableDataByIndex, originalTableDataByIndex },
        });
      } else {
        dispatch({
          type: "SET_MESSAGE",
          payload: "No data available for this report",
        });
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      dispatch({ type: "SET_MESSAGE", payload: "Error fetching report data" });
      showError("Failed to fetch report data. Please try again.");
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
      setIsGlobalLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [reportId]);

  // Fetch Excel data for autocomplete suggestions
  useEffect(() => {
    const fetchExcelData = async () => {
      if (!reportId || !state.tables || state.tables.length === 0) return;

      try {
        const excelDataPromises = state.tables.map(table =>
          getExcelDataByReportAPI(reportId, table.id.toString())
        );
        
        const excelDataResults = await Promise.all(excelDataPromises);
        
        // Map Excel data by table ID
        const excelDataMap = {};
        state.tables.forEach((table, index) => {
          const tableExcelData = excelDataResults[index];
          
          if (tableExcelData && tableExcelData.length > 0) {
            // Use the most recent Excel data entry
            excelDataMap[table.id] = tableExcelData[0].data;
          }
        });
        
        setExcelDataByTable(excelDataMap);
      } catch (error) {
        console.error("Error fetching Excel data:", error);
      }
    };

    fetchExcelData();
  }, [reportId, state.tables]);

  // Track used values to exclude from suggestions
  useEffect(() => {
    const usedValues = {};
    
    // Iterate through all tables and collect used values
    Object.keys(state.tableDataByIndex).forEach((tableIndex) => {
      const tableData = state.tableDataByIndex[tableIndex] || [];
      const table = state.tables[tableIndex];
      
      if (!table) return;
      
      usedValues[table.id] = {};
      
      // For each column, collect all used values with their counts
      (table.columns || []).forEach((column, colIndex) => {
        const columnTitle = column.title;
        const usedInColumn = []; // Array to track all used values (including duplicates)
        
        tableData.forEach((row) => {
          const cellValue = row.row?.[colIndex]?.value;
          if (cellValue && cellValue.toString().trim()) {
            usedInColumn.push(cellValue.toString().trim());
          }
        });
        
        usedValues[table.id][columnTitle] = usedInColumn; // Store array with duplicates
      });
    });
    
    setUsedValuesByTable(usedValues);
  }, [state.tableDataByIndex, state.tables]);

  // Submit handler
  const SubmitUpdateHandler = async () => {
    handleSave("submited");
  };

  // Export payload
  const exportPayload = useMemo(() => {
    const payload = Array.isArray(state.reportData)
      ? state.reportData
      : [state.reportData];
    const headerMapping = {
      companyLocation: "Company Location",
      companyName: "Company Name",
      companyId: "Company ID",
    };
    return payload.map(({ tables, ...rest }) => {
      const renamedObject = {};
      Object.keys(rest).forEach((key) => {
        const newKey = headerMapping[key] || key;
        renamedObject[newKey] = rest[key];
      });
      return renamedObject;
    });
  }, [state.reportData]);

  // Document handlers
  const handleDocumentInputChange = useCallback(
    (e, tableDocumentIndex, index) => {
      const value = e.target.value;
      const updatedTableData = [...(state.tableDataByIndex[index] || [])];
      if (!updatedTableData.length || !updatedTableData[0]?.row) return;

      const format = updatedTableData[0].row[tableDocumentIndex]?.format || "";

      let newValue = value;
      if (format === "date") {
        const [year, month, day] = value.split("-");
        newValue = `${month}/${day}/${year}`;
      } else if ((format || "").toLowerCase() === "number") {
        const sanitized = String(value).replace(/[^0-9.-]/g, "").replace(/(\..*)\./g, "$1");
        newValue = sanitized === "" || sanitized === "-" ? "" : Number(sanitized);
      }

      updatedTableData[0].row[tableDocumentIndex] = {
        ...updatedTableData[0].row[tableDocumentIndex],
        value: newValue,
        time: Date.now().toString(),
      };

      dispatch({ type: "SET_TABLE_DATA", index, payload: updatedTableData });
    },
    [state.tableDataByIndex]
  );

  const handleDocumentCancel = () => {
    const updatedTableDataByIndex = {};
    Object.keys(state.originalTableDataByIndex).forEach((index) => {
      updatedTableDataByIndex[index] = JSON.parse(
        JSON.stringify(state.originalTableDataByIndex[index] || [])
      );
    });
    dispatch({
      type: "SET_TABLE_DATA",
      index: null,
      payload: updatedTableDataByIndex,
    });
    dispatch({ type: "TOGGLE_EDITABLE", payload: false });
    fetchReportData();
  };

  const handleSave = async (type) => {
    try {
      setIsGlobalLoading(true);
      // setGlobalLoadingText(type === "submited" ? "Submitting report..." : "Saving changes...");
      
      const updatedFilledData = (state.reportData.filledData || []).map(
        (item, i) => ({
          ...item,
          table: state.tableDataByIndex[i] || [],
        })
      );

      let data = JSON.stringify({
        query: `mutation UpdateReportsBySecurity($templateId: ID!, $updatedData: UpdateReportInput!) {
          updateReportsBySecurity(templateId: $templateId, updatedData: $updatedData) {
            success
            status
            message
            data
          }
        }`,
        variables: {
          templateId: reportId,
          updatedData: {
            filledData: updatedFilledData,
            ...(type === "submited" && {
              isSubmitted: true,
              isSubmittedByManagement: true,

            }),
           submittedAt: new Date().toISOString(),
           
          },
        },
      });

      let config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${API_URL}/graphql`,
        headers: {
          "Content-Type": "application/json",
          Authorization: UserType?.token,
        },
        data: data,
      };

      axios
        .request(config)
        .then((response) => {
          console.log(JSON.stringify(response.data));
          fetchReportData();
          dispatch({ type: "TOGGLE_EDITABLE", payload: false });
          dispatch({ type: "TOGGLE_SUBMIT", payload: false });
          showAlert(type === "submited" ? "Report submitted successfully!" : "Changes saved successfully!", "success");
        })
        .catch((error) => {
          console.log(error);
          showError("Failed to save changes. Please try again.");
        });
    } catch (error) {
      console.error("Error updating report:", error);
      showError("Error saving changes. Please try again.");
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const formatDateToInput = (dateString) => {
    console.log("dateString", dateString);
    if (!dateString) return "";
    const parts = dateString.split("/");
    if (parts.length !== 3) return dateString;
    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  // Helper function to normalize format strings (case-insensitive comparison)
  const normalizeFormat = (format) => {
    if (!format || typeof format !== "string") return "";
    return format.toLowerCase().trim().replace(/\s+/g, " ");
  };

  // Helper function to check if format matches (case-insensitive)
  const isFormat = (format, ...targets) => {
    const normalized = normalizeFormat(format);
    return targets.some(target => normalizeFormat(target) === normalized);
  };

  // Helper function to find Excel data key case-insensitively
  const findExcelDataKey = (excelData, columnTitle) => {
    if (!excelData || !columnTitle || columnTitle === "masterEntries") return null;
    const normalizedTitle = String(columnTitle).toLowerCase().trim();
    const key = Object.keys(excelData).find(k => k.toLowerCase().trim() === normalizedTitle);
    return key || null;
  };

  // Helper function to get autocomplete suggestions for a field
  const getSuggestions = (tableId, columnTitle, currentValue) => {
    const excelData = excelDataByTable[tableId];
    if (!excelData || columnTitle === "masterEntries") return [];
    const excelKey = findExcelDataKey(excelData, columnTitle);
    if (!excelKey) return [];
    const availableValues = excelData[excelKey];
    if (!availableValues || !Array.isArray(availableValues)) return [];
    const uniqueValues = [...new Set(availableValues.map((value) => value.toString().trim()))];
    return uniqueValues;
  };

  // const renderDocumentPreview = (
  //   tableDocumentData,
  //   tableDocumentIndex,
  //   filterTablebySequence,
  //   index
  // ) => {
  //   console.log("tableDocumentData", tableDocumentData.value);
  //   if (!tableDocumentData) return null;

  //   const widthFull = filterTablebySequence[0]?.width === "100%";
  //   const haveImage =
  //     tableDocumentData.format === "photo upload" ||
  //     tableDocumentData.format === "photo capture";
  //   const isEditable = state.isEditable;

  //   return (
  //     <div
  //       className={`grid text-nowrap items-start gap-1 ${
  //         widthFull ? "grid-cols-4" : "grid-cols-2"
  //       }`}
  //     >
  //       <div>
  //         <div className="font-semibold border  px-2 p-1 bg-zinc-100">
  //           {tableDocumentData.title || "Empty Title"}
  //         </div>
  //         {haveImage && isEditable && (
  //           <div className="p-4">
  //             <input
  //               type="file"
  //               multiple
  //               accept="image/*"
  //               onChange={(e) =>
  //                 handleImageUpload(e, 0, tableDocumentIndex, index)
  //               }
  //               className="text-sm"
  //             />
  //           </div>
  //         )}
  //       </div>
  //       <div
  //         className={`${widthFull ? "col-span-3" : ""}`}
  //         style={
  //           tableDocumentData.border === "yes" ? { borderWidth: "1px" } : {}
  //         }
  //       >
  //         {tableDocumentData.format === "Yes/No" ? (
  //           <select
  //             value={tableDocumentData.value || "false"}
  //             onChange={(e) =>
  //               handleDocumentInputChange(e, tableDocumentIndex, index)
  //             }
  //             disabled={!isEditable}
  //             className="w-full border  p-1"
  //           >
  //             <option value="true">Yes</option>
  //             <option value="false">No</option>
  //           </select>
  //         ) : haveImage ? (
  //           <div className="flex flex-wrap w-full gap-2">
  //             {Array.isArray(tableDocumentData.value) &&
  //               tableDocumentData.value.map((imagesSrc, imgIndex) => (
  //                 <div key={imgIndex} className="relative">
  //                   <img src={imagesSrc} alt="img" className="max-w-[180px]" />
  //                   {isEditable && (
  //                     <button
  //                       onClick={() =>
  //                         handleRemoveImage(
  //                           0,
  //                           tableDocumentIndex,
  //                           imgIndex,
  //                           index
  //                         )
  //                       }
  //                       className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
  //                     >
  //                       ×
  //                     </button>
  //                   )}
  //                 </div>
  //               ))}
  //           </div>
  //         ) : tableDocumentData.format === "f/c" ? (
  //           <input
  //             value={tableDocumentData.value || ""}
  //             onChange={(e) =>
  //               handleDocumentInputChange(e, tableDocumentIndex, index)
  //             }
  //             type="text"
  //             className="w-full border  p-1"
  //             disabled={!isEditable}
  //           />
  //         ) : (
  //           <>
  //             {/* {tableDocumentData.format === "date"
  //               ? formatDateToInput(tableDocumentData.value)
  //               : tableDocumentData.value || ""}
  //             {tableDocumentData.value} */}
  //             <input
  //               value={
  //                 tableDocumentData.format === "date"
  //                   ? formatDateToInput(tableDocumentData.value)
  //                   : tableDocumentData.value || ""
  //               }
  //               onChange={(e) =>
  //                 handleDocumentInputChange(e, tableDocumentIndex, index)
  //               }
  //               type={
  //                 tableDocumentData.format === "date"
  //                   ? "date"
  //                   : tableDocumentData.format === "time"
  //                   ? "time"
  //                   : tableDocumentData.format === "number"
  //                   ? "number"
  //                   : "text"
  //               }
  //               className={`w-full border   ${
  //                 tableDocumentData.format === "date" ||
  //                 tableDocumentData.format === "time"
  //                   ? "p-[3px]"
  //                   : "p-1"
  //               }`}
  //               disabled={!isEditable}
  //             />
  //           </>
  //         )}
  //         {tableDocumentData.time && (
  //           <div className="text-[0.7rem] text-zinc-400">
  //             {MomentFormatDate(tableDocumentData.time)}
  //           </div>
  //         )}
  //       </div>
  //     </div>
  //   );
  // };

  const renderDocumentPreview = (
    tableDocumentData,
    tableDocumentIndex,
    filterTablebySequence,
    index
  ) => {
    // console.log("tableDocumentData", tableDocumentData.value);
    if (!tableDocumentData) return null;

    const columnConfig = filterTablebySequence[0] || {};
    // Width is handled by parent container now, but grid logic inside might still rely on it for layout
    const widthFull = columnConfig.width === "100" || columnConfig.width === 100 || columnConfig.width === "100%";
    const showBorder = columnConfig.border !== "No" && columnConfig.border !== "no";

    const haveImage = isFormat(
      tableDocumentData.format,
      "photo upload",
      "photo capture",
      "Photo Upload",
      "Photo Capture",
      "photo Upload",
      "photo Capture"
    );
    const isEditable = state.isEditable;

    const commonInputClass = `w-full p-1 ${
      showBorder ? "border" : "border-none"
    } ${isEditable ? "bg-gray-50" : "bg-white"}`;

    return (
      <div
        className={`grid text-nowrap items-start gap-1 text-sm ${
          widthFull ? "grid-cols-4 w-full" : "grid-cols-2"
        }`}
      >
        <div>
          <div className="font-semibold capitalize border px-2 p-1 bg-[#004278] text-white">
            {tableDocumentData.title || "Empty Title"}
          </div>
          {haveImage && isEditable && (
            <div className="p-4">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, 0, tableDocumentIndex, index)
                }
                className="text-sm"
              />
            </div>
          )}
        </div>
        <div
          className={`${widthFull ? "col-span-3" : ""} ${
            isFormat(tableDocumentData.format, "checkbox") ? "self-center" : ""
          }`}
        >
          {isFormat(tableDocumentData.format, "Yes/No", "yes/no") ? (
            <select
              value={tableDocumentData.value || "false"}
              onChange={(e) =>
                handleDocumentInputChange(e, tableDocumentIndex, index)
              }
              disabled={!isEditable}
              className={commonInputClass}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : isFormat(tableDocumentData.format, "checkbox") ? (
            <div className="pl-1 flex items-center min-h-[30px]">
              <input
                type="checkbox"
                checked={
                  tableDocumentData.value === "true" ||
                  tableDocumentData.value === true
                }
                onChange={(e) =>
                  handleDocumentInputChange(
                    { target: { value: e.target.checked ? "true" : "false" } },
                    tableDocumentIndex,
                    index
                  )
                }
                disabled={!isEditable}
                className="w-4 h-4 cursor-pointer"
              />
            </div>
          ) : haveImage ? (
            <div className="flex flex-wrap w-full gap-2">
              {Array.isArray(tableDocumentData.value) &&
                tableDocumentData.value.map((imagesSrc, imgIndex) => (
                  <div key={imgIndex} className="relative">
                    <img src={imagesSrc} alt="img" className="max-w-[180px]" />
                    {isEditable && (
                      <button
                        onClick={() =>
                          handleRemoveImage(
                            0,
                            tableDocumentIndex,
                            imgIndex,
                            index
                          )
                        }
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
            </div>
          ) : isFormat(tableDocumentData.format, "f&c", "F&C", "f/c", "F/C") ? (
            <select
              value={tableDocumentData.value || ""}
              onChange={(e) =>
                handleDocumentInputChange(e, tableDocumentIndex, index)
              }
              disabled={!isEditable}
              className={commonInputClass}
            >
              <option value="">Select...</option>
              <option value="F">F</option>
              <option value="C">C</option>
            </select>
          ) : isFormat(
              tableDocumentData.format,
              "Text",
              "text",
              "long text",
              "textarea"
            ) ? (
            <AutocompleteInput
              value={tableDocumentData.value || ""}
              className={`w-full resize-none overflow-y-auto p-[0.3rem] ${
                showBorder ? "border" : "border-none"
              } ${isEditable ? "bg-gray-50" : "bg-white"}`}
              onChange={(e) => {
                // Auto-resize textarea
                if (e.target && e.target.style) {
                  e.target.style.height = "auto";
                  e.target.style.height = e.target.scrollHeight + "px";
                }
                handleDocumentInputChange(e, tableDocumentIndex, index);
              }}
              disabled={!isEditable}
              isTextarea={true}
              rows={1}
              suggestions={getSuggestions(
                state.tables[0]?.id,
                tableDocumentData.title,
                tableDocumentData.value
              )}
            />
          ) : isFormat(tableDocumentData.format, "e-sign") ? (
            <ESignatureCell
              width={200}
              value={tableDocumentData.value}
              isEditable={isEditable}
              onChange={async (val) => {
                // If val is a dataURL, upload to Firebase and save the link
                if (
                  val &&
                  typeof val === "string" &&
                  val.startsWith("data:image")
                ) {
                  // Convert dataURL to Blob
                  const res = await fetch(val);
                  const blob = await res.blob();
                  const { uploadImageToFirebase } = await import(
                    "../../apis/firebase_bucket"
                  );
                  const url = await uploadImageToFirebase(blob, "e-signatures");
                  handleDocumentInputChange(
                    { target: { value: url } },
                    tableDocumentIndex,
                    index
                  );
                } else {
                  handleDocumentInputChange(
                    { target: { value: val } },
                    tableDocumentIndex,
                    index
                  );
                }
              }}
              onSignatureSaved={() => {
                dispatch({ type: "TOGGLE_SIGNATURE_POPUP", payload: true });
                // Auto-hide popup after 3 seconds
                setTimeout(() => {
                  dispatch({ type: "TOGGLE_SIGNATURE_POPUP", payload: false });
                }, 3000);
              }}
            />
          ) : isFormat(
              tableDocumentData.format,
              "time military",
              "time am/pm"
            ) ? (
            (() => {
              const isMilitaryFormat = isFormat(
                tableDocumentData.format,
                "time military"
              );
              const defaultValue = isMilitaryFormat ? "00:00" : "12:00 AM";

              const timeValue =
                tableDocumentData.value &&
                typeof tableDocumentData.value === "string"
                  ? tableDocumentData.value
                  : defaultValue;

              try {
                return (
                  isEditable || tableDocumentData.value ? (
                    <TimePicker
                      value={timeValue}
                      onChange={(val) => {
                        if (val !== null && val !== undefined) {
                          handleDocumentInputChange(
                            { target: { value: val } },
                            tableDocumentIndex,
                            index
                          );
                        }
                      }}
                      disabled={!isEditable}
                      use12Hours={!isMilitaryFormat}
                      className="w-full  react-ios-time-picker-input"
                      style={{ border: "none" }}
                    />
                  ) : (
                    <input
                      type="time"
                      value={timeValue}
                      onChange={(e) => {
                        handleDocumentInputChange(
                          e,
                          tableDocumentIndex,
                          index
                        );
                      }}
                      disabled={!isEditable}
                      style={{ border: "none" }}
                      className={`w-full border-none p-1 ${
                        isEditable ? "bg-gray-50" : "bg-white"
                      }`}
                    />
                  )
                );
              } catch (error) {
                console.error("TimePicker error:", error);
                // Fallback to a regular input if TimePicker fails
                return (
                  <input
                    type="time"
                    value={timeValue}
                    onChange={(e) => {
                      handleDocumentInputChange(e, tableDocumentIndex, index);
                    }}
                    disabled={!isEditable}
                    className={commonInputClass}
                  />
                );
              }
            })()
          ) : (
            <>
              <AutocompleteInput
                value={
                  isFormat(tableDocumentData.format, "date", "Date")
                    ? formatDateToInput(tableDocumentData.value)
                    : tableDocumentData.value || ""
                }
                onChange={(e) =>
                  handleDocumentInputChange(e, tableDocumentIndex, index)
                }
                type={
                  isFormat(tableDocumentData.format, "date", "Date")
                    ? "date"
                    : isFormat(tableDocumentData.format, "time")
                    ? "time"
                    : isFormat(tableDocumentData.format, "number", "Number")
                    ? "number"
                    : "text"
                }
                className={`${commonInputClass} ${
                  isFormat(tableDocumentData.format, "date", "Date", "time")
                    ? "p-[3px]"
                    : "p-1"
                }`}
                disabled={!isEditable}
                suggestions={
                  isFormat(tableDocumentData.format, "date", "Date", "time", "number", "Number")
                    ? [] // No suggestions for date/time/number fields
                    : getSuggestions(
                        state.tables[0]?.id,
                        tableDocumentData.title,
                        tableDocumentData.value
                      )
                }
              />
            </>
          )}

          {state?.reportData?.permission?.accessType === "viewLiveFullAccess" &&
            tableDocumentData.time && (
              <div className="text-xs text-zinc-400 ">
                {MomentFormatDate(tableDocumentData.time)}
              </div>
            )}
        </div>
      </div>
    );
  };

  // Helper function to autofill other columns in the same row when a suggestion is selected.
  // Only master columns (masterEntries) trigger full-row fill; non-master only update the clicked cell.
  const handleAutofillOtherColumns = useCallback(
    (selectedValue, contentIndex, tableTabularIndex, tableIndex) => {
      const tableId = state.tables[tableIndex]?.id;
      const excelData = excelDataByTable[tableId];
      const table = state.tables[tableIndex];
      
      if (!excelData || !table || !table.columns) return;
      
      const masterEntries = Array.isArray(excelData.masterEntries) ? excelData.masterEntries : [];
      const currentColumn = table.columns[tableTabularIndex];
      if (!currentColumn) return;
      
      // Case-insensitive lookup for current column in Excel data
      const currentExcelKey = findExcelDataKey(excelData, currentColumn.title);
      if (!currentExcelKey) return;
      
      // Case-insensitive check: normalize masterEntries and column title to lowercase for comparison
      const normalizedMasterEntries = masterEntries.map(e => String(e).toLowerCase().trim());
      const normalizedColumnTitle = String(currentColumn.title).toLowerCase().trim();
      const isMasterColumn = normalizedMasterEntries.length > 0 && normalizedMasterEntries.includes(normalizedColumnTitle);
      
      // Non-master column: only the clicked cell is filled (by the dropdown); do not fill Name or other columns
      if (!isMasterColumn) return;
      
      // Master column: fill the rest of the current row from the selected Excel row (e.g. selecting Name fills Age)
      const columnValues = excelData[currentExcelKey] || [];
      const rowIndex = columnValues.findIndex(
        (val) => val && val.toString().trim() === selectedValue.toString().trim()
      );
      
      if (rowIndex === -1) return;
      
      const updatedTableData = [...(state.tableDataByIndex[tableIndex] || [])];
      if (!updatedTableData.length || !updatedTableData[contentIndex]?.row) return;
      
      let hasChanges = false;
      table.columns.forEach((column, colIdx) => {
        if (colIdx === tableTabularIndex) return;
        
        // Case-insensitive lookup for each column in Excel data
        const excelKey = findExcelDataKey(excelData, column.title);
        if (!excelKey || !Array.isArray(excelData[excelKey])) return;
        if (rowIndex >= excelData[excelKey].length) return;
        
        const cellData = updatedTableData[contentIndex].row[colIdx];
        if (!cellData) return;
        
        const format = (cellData.format || "").toLowerCase();
        const isAutocompleteField =
          format === "textarea" ||
          format === "text" ||
          format === "long text" ||
          (format !== "date" &&
            format !== "time" &&
            format !== "number" &&
            format !== "f&c" &&
            format !== "yes/no" &&
            format !== "checkbox" &&
            format !== "fixed value" &&
            format !== "ref");
        const isNumberField = format === "number";

        if (isAutocompleteField || isNumberField) {
          const correspondingValue = excelData[excelKey][rowIndex];
          if (
            correspondingValue !== undefined &&
            correspondingValue !== null &&
            correspondingValue !== ""
          ) {
            let valueToStore = correspondingValue;
            if (isNumberField) {
              const num = Number(String(correspondingValue).replace(/[^0-9.-]/g, ""));
              valueToStore = isNaN(num) ? correspondingValue : num;
            } else {
              valueToStore = correspondingValue.toString();
            }
            updatedTableData[contentIndex].row[colIdx] = {
              ...updatedTableData[contentIndex].row[colIdx],
              value: valueToStore,
              time: Date.now().toString(),
            };
            hasChanges = true;
          }
        }
      });
      
      if (hasChanges) {
        dispatch({ type: "SET_TABLE_DATA", index: tableIndex, payload: updatedTableData });
      }
    },
    [state.tableDataByIndex, state.tables, excelDataByTable, dispatch]
  );

  // Table handlers
  const handleTableInputChange = useCallback(
    (e, contentIndex, tableTabularIndex, index) => {
      const value = e.target.value;
      const updatedTableData = [...(state.tableDataByIndex[index] || [])];
      if (!updatedTableData.length || !updatedTableData[contentIndex]?.row)
        return;

      const existingCell =
        updatedTableData[contentIndex].row[tableTabularIndex];
      const column = state.tables[index]?.columns?.[tableTabularIndex];
      const format = existingCell?.format ?? column?.format ?? "";

      let newValue = value;
      const isNumberFormat = (format || "").toLowerCase() === "number";
      if (isNumberFormat) {
        const sanitized = String(value).replace(/[^0-9.-]/g, "").replace(/(\..*)\./g, "$1");
        newValue = sanitized === "" || sanitized === "-" ? "" : Number(sanitized);
      } else if (format === "date") {
        const [year, month, day] = value.split("-");
        newValue = `${month}/${day}/${year}`;
      }

      const baseCell =
        existingCell && typeof existingCell === "object"
          ? existingCell
          : {
              format: column?.format,
              title:
                column?.format === "fixed value"
                  ? column?.fixed?.[contentIndex] ?? ""
                  : column?.title ?? "",
              value: column?.format === "Yes/No" ? "false" : "",
              time: Date.now().toString(),
            };

      updatedTableData[contentIndex].row[tableTabularIndex] = {
        ...baseCell,
        value: newValue,
        time: Date.now().toString(),
      };

      dispatch({ type: "SET_TABLE_DATA", index, payload: updatedTableData });
    },
    [state.tableDataByIndex, state.tables]
  );

  const handleAddRow = (index) => {
    const columns = state.tables[index]?.columns || [];
    const currentTimestamp = Date.now().toString();
    const newRow = {
      id: `row-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Add stable unique ID
      row: columns.map((item) => ({
        format: item.format,
        title: item.format === "fixed value"
          ? (state.tableDataByIndex[index]?.length < item.fixed?.length
              ? item.fixed[state.tableDataByIndex[index].length]
              : "")
          : (item.title || ""), // Set the column title for all formats
        value: item.format === "Yes/No" ? "false" : "",
        time: currentTimestamp, // Add timestamp for all cell types including ref
      })),
    };
    const updatedTableData = [...(state.tableDataByIndex[index] || []), newRow];
    dispatch({ type: "SET_TABLE_DATA", index, payload: updatedTableData });
  };

  const DeleteTabularHandler = (contentIndex, index) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this row?"
    );

    if (confirmDelete) {
      deleteRowHandler(contentIndex, index);
    }
  };

  const deleteRowHandler = (contentIndex, index) => {
    const updatedTableData = [...(state.tableDataByIndex[index] || [])];
    if (updatedTableData[contentIndex]) {
      updatedTableData.splice(contentIndex, 1);
      dispatch({ type: "SET_TABLE_DATA", index, payload: updatedTableData });
    }
  };

  const handleImageUpload = async (
    e,
    contentIndex,
    tableTabularIndex,
    index
  ) => {
    const files = Array.from(e.target.files);
    dispatch({ type: "SET_UPLOADING", index, payload: true });
    try {
      // Use uploadImageToFirebase for each file
      const { uploadImageToFirebase } = await import(
        "../../apis/firebase_bucket"
      );
      const uploadPromises = files.map(async (file) => {
        return await uploadImageToFirebase(file, "reportImages");
      });
      const imageUrls = await Promise.all(uploadPromises);
      const updatedTableData = [...(state.tableDataByIndex[index] || [])];
      if (!updatedTableData[contentIndex]?.row) return;

      if (
        !Array.isArray(
          updatedTableData[contentIndex].row[tableTabularIndex].value
        )
      ) {
        updatedTableData[contentIndex].row[tableTabularIndex].value = [];
      }
      updatedTableData[contentIndex].row[tableTabularIndex].value = [
        ...updatedTableData[contentIndex].row[tableTabularIndex].value,
        ...imageUrls,
      ];
      updatedTableData[contentIndex].row[tableTabularIndex].time =
        Date.now().toString();
      dispatch({ type: "SET_TABLE_DATA", index, payload: updatedTableData });
    } catch (error) {
      console.error("Error uploading images:", error);
      alert("Error uploading images. Please try again.");
    } finally {
      dispatch({ type: "SET_UPLOADING", index, payload: false });
    }
  };

  const handleRemoveImage = (
    contentIndex,
    tableTabularIndex,
    imageIndex,
    index
  ) => {
    const updatedTableData = [...(state.tableDataByIndex[index] || [])];
    if (!updatedTableData[contentIndex]?.row) return;

    const images = [
      ...updatedTableData[contentIndex].row[tableTabularIndex].value,
    ];
    images.splice(imageIndex, 1);
    updatedTableData[contentIndex].row[tableTabularIndex].value = images;
    updatedTableData[contentIndex].row[tableTabularIndex].time =
      Date.now().toString();
    dispatch({ type: "SET_TABLE_DATA", index, payload: updatedTableData });
  };

  const renderTableCell = (
    tableTabularData,
    contentIndex,
    tableTabularIndex,
    index,
    listeners = null,
    attributes = null,
    columnDef = null
  ) => {
    if (!tableTabularData || typeof tableTabularData !== 'object') return null;

    // console.log("tableTabularData.format", tableTabularData.format === "f&c"? tableTabularData.format: "--");
    const isEditable = state.isEditable;
    
    // Default style classes
    const commonInputClass = `w-full p-1  ${
      isEditable ? "bg-gray-100" : "bg-white"
    }`;

    if (tableTabularData.format === "ref") {
      return (
        <div className="flex justify-center gap-2 items-center">
          <button
            {...(listeners || {})}
            {...(attributes || {})}
            className={`cursor-grab active:cursor-grabbing transition-colors duration-200 p-1 rounded ${
              state.isEditable
                ? "text-gray-600 hover:text-blue-600 hover:bg-blue-50 block"
                : "text-gray-300 cursor-not-allowed hidden"
            }`}
            disabled={!state.isEditable}
            title={state.isEditable ? "Drag to reorder" : "Edit mode required"}
          >
            <MdOutlineDragIndicator />
          </button>
          <span className="font-medium">{contentIndex + 1}</span>
        </div>
      );
    }
    if (tableTabularData.format === "f&c") {
      return (
        <select
          value={tableTabularData.value || ""}
          onChange={(e) =>
            handleTableInputChange(e, contentIndex, tableTabularIndex, index)
          }
          disabled={!isEditable}
          className={commonInputClass}
        >
          <option value="">Select</option>
          <option value="F">F</option>
          <option value="C">C</option>
        </select>
      );
    }

    if (tableTabularData.format === "Yes/No") {
      return (
        <select
          value={tableTabularData.value || "false"}
          disabled={!isEditable}
          onChange={(e) =>
            handleTableInputChange(e, contentIndex, tableTabularIndex, index)
          }
          className={commonInputClass}
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (normalizeTabularFormat(tableTabularData.format) === "tour") {
      const tableColumns = state.tables[index]?.columns || [];
      const checkpointName = getCheckpointNameForRow(
        columnDef,
        tableTabularData,
        contentIndex,
        tableColumns
      );
      return (
        <div className="w-full min-h-[56px] p-1 font-medium text-zinc-700 text-center flex items-center justify-center">
          {checkpointName || "-"}
        </div>
      );
    }

    if (normalizeTabularFormat(tableTabularData.format) === "qrcode") {
      const timeStr =
        tableTabularData.time != null ? String(tableTabularData.time).trim() : "";
      const isDisplayTime = /^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}(am|pm)$/i.test(
        timeStr
      );

      // Show mapped cell time only when it is in expected display format.
      if (isDisplayTime) {
        return (
          <div className="w-full min-h-[56px] flex flex-col justify-center items-center text-center text-sm text-zinc-800 px-1 py-1">
            <span className="leading-snug">{timeStr}</span>
          </div>
        );
      }

      let qrValue = String(
        tableTabularData?.value ||
          tableTabularData?.tour?.qrcode ||
          tableTabularData?.tour?.location ||
          ""
      ).trim();

      if (!qrValue && columnDef) {
        const fixedCheckpoint = parseCheckpointFixedAtRow(columnDef, contentIndex);
        qrValue = String(fixedCheckpoint?.qrcode || fixedCheckpoint?.location || "").trim();
      }

      if (!qrValue) {
        return <span className="text-zinc-400 text-xs">-</span>;
      }

      return (
        <div className="h-14 w-14 border rounded bg-white p-1 mx-auto flex items-center justify-center">
          <QRCode
            value={qrValue}
            size={48}
            bgColor="#ffffff"
            fgColor="#000000"
            level="L"
          />
        </div>
      );
    }

    if (tableTabularData.format === "fixed value") {
      const fixedDisplay =
        formatFixedValueDisplay(tableTabularData.title) ||
        tableTabularData.title ||
        (columnDef
          ? formatFixedValueDisplay(columnDef.fixed?.[contentIndex])
          : "");
      return (
        <input
          value={fixedDisplay}
          onChange={(e) =>
            handleTableInputChange(e, contentIndex, tableTabularIndex, index)
          }
          disabled={!isEditable}
          className={`w-full border p-1   text-left  ${
            isEditable ? "bg-gray-100" : "bg-white"
          }`}
        />
      );
    }

    if (
      tableTabularData.format === "photo Upload" ||
      tableTabularData.format === "Photo Upload" ||
      tableTabularData.format === "Photo Capture" || 
      tableTabularData.format === "photo Capture" 
    ) {
      return (
        <div className="space-y-2 w-full">
          {isEditable && (
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) =>
                handleImageUpload(e, contentIndex, tableTabularIndex, index)
              }
              disabled={state.uploadingByIndex[index]}
              className="text-sm"
            />
          )}
          <div className="flex flex-wrap gap-2 justify-center">
            {Array.isArray(tableTabularData.value) &&
              tableTabularData.value.map((imagesSrc, imgIndex) => (
                <div key={imgIndex} className="relative">
                  <img
                    src={imagesSrc}
                    alt={`uploaded-${imgIndex}`}
                    className="max-w-[200px] h-auto"
                  />
                  {isEditable && (
                    <button
                      onClick={() =>
                        handleRemoveImage(
                          contentIndex,
                          tableTabularIndex,
                          imgIndex,
                          index
                        )
                      }
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
          </div>
        </div>
      );
    }
    
    if (tableTabularData.format === "e-sign") {
      console.log("tableTabularData.value", tableTabularData.value);
      return (
        <ESignatureCell
          width={200}
          value={tableTabularData.value}
          isEditable={isEditable}
          onChange={async (val) => {
            // If val is a dataURL, upload to Firebase and save the link
            if (
              val &&
              typeof val === "string" &&
              val.startsWith("data:image")
            ) {
              // Convert dataURL to Blob
              const res = await fetch(val);
              const blob = await res.blob();
              const { uploadImageToFirebase } = await import(
                "../../apis/firebase_bucket"
              );
              const url = await uploadImageToFirebase(blob, "e-signatures");
              handleTableInputChange(
                { target: { value: url } },
                contentIndex,
                tableTabularIndex,
                index
              );
            } else {
              handleTableInputChange(
                { target: { value: val } },
                contentIndex,
                tableTabularIndex,
                index
              );
            }
          }}
          onSignatureSaved={() => {
            dispatch({ type: "TOGGLE_SIGNATURE_POPUP", payload: true });
            // Auto-hide popup after 3 seconds
            setTimeout(() => {
              dispatch({ type: "TOGGLE_SIGNATURE_POPUP", payload: false });
            }, 3000);
          }}
        />
      );
    }

    if (tableTabularData.format === "checkbox") {
      return (
        <div className="flex items-center justify-center space-x-2">
          <input
            type="checkbox"
            checked={tableTabularData.value === true}
            onChange={(e) =>
              handleTableInputChange(
                { target: { value: e.target.checked ? true : false } },
                contentIndex,
                tableTabularIndex,
                index
              )
            }
            disabled={!isEditable}
            className="w-4 h-4"
          />
        </div>
      );
    }

    if (
      tableTabularData.format &&
      (tableTabularData.format === "time military" ||
      tableTabularData.format === "time am/pm")
    ) {
      const isMilitaryFormat = tableTabularData.format === "time military";
      const defaultValue = isMilitaryFormat ? "00:00" :"12:00 AM";
      
      const timeValue = tableTabularData.value && typeof tableTabularData.value === 'string' 
        ? tableTabularData.value 
        : defaultValue;
      
      // console.log("timeValue", isMilitaryFormat? "":timeValue,"defaultValue",isMilitaryFormat ?"": defaultValue);
      try {
        return (
          (isEditable || tableTabularData.value ) ? <TimePicker
            value={timeValue}
            onChange={(val) => {
              // Add null check for the onChange value
              if (val !== null && val !== undefined) {
                handleTableInputChange(
                  { target: { value: val } },
                  contentIndex,
                  tableTabularIndex,
                  index
                );
              }
            }}
            disabled={!isEditable}
            use12Hours={!isMilitaryFormat}
            className="w-full border-none bg-gray-50"
            style={{ border: "none", outline: "none" }}
          /> :  <input
          type="time"
          // value={timeValue}
          onChange={(e) => {
            handleTableInputChange(e, contentIndex, tableTabularIndex, index);
          }}
          disabled={!isEditable}
          className={`w-full  p-1    ${
            isEditable ? "bg-gray-100" : "bg-white"
          }`}
        />
        );
      } catch (error) {
        console.error('TimePicker error:', error);
        // Fallback to a regular input if TimePicker fails
        return (
          <input
            type="time"
            value={timeValue}
            onChange={(e) => {
              handleTableInputChange(e, contentIndex, tableTabularIndex, index);
            }}
            disabled={!isEditable}
            className={`w-full border p-1    ${
              isEditable ? "bg-gray-100" : "bg-white"
            }`}
          />
        );
      }
    }



    if (
     ( tableTabularData.format === "textarea" || tableTabularData.format === "Text" || tableTabularData.format === "long text")
    ) {
      return (
        <AutocompleteInput
          value={tableTabularData.value || ""}
          className={`w-full  resize-none overflow-hidden p-[0.32rem]  ${
            isEditable ? "bg-gray-100" : "bg-white"
          }`}
          onChange={(e) => {
            // Auto-resize textarea only if e.target exists and has style property
            if (e.target && e.target.style) {
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }
            handleTableInputChange(e, contentIndex, tableTabularIndex, index);
          }}
          onSuggestionSelect={(selectedValue) => {
            handleAutofillOtherColumns(selectedValue, contentIndex, tableTabularIndex, index);
          }}
          disabled={!isEditable}
          isTextarea={true}
          rows={1}
          suggestions={getSuggestions(
            state.tables[index]?.id,
            tableTabularData.title,
            tableTabularData.value
          )}
        />
      );
    }

    return (
      <AutocompleteInput
        value={
          tableTabularData.format === "date"
            ? formatDateToInput(tableTabularData.value)
            : (tableTabularData.value ?? "")
        }
        type={
          tableTabularData.format === "time"
            ? "time"
            : (tableTabularData.format || "").toLowerCase() === "number"
            ? "number"
            : tableTabularData.format === "date"
            ? "date"
            : tableTabularData.format === "Text"
            ? ""
            : ""
        }
        onChange={(e) =>
          handleTableInputChange(e, contentIndex, tableTabularIndex, index)
        }
        onSuggestionSelect={(selectedValue) => {
          handleAutofillOtherColumns(selectedValue, contentIndex, tableTabularIndex, index);
        }}
        disabled={!isEditable}
        className={`w-full border p-1  text-left ${
          isEditable ? "bg-gray-100" : "bg-white"
        }`}
        suggestions={
          tableTabularData.format === "date" || tableTabularData.format === "time"
            ? [] // No suggestions for date/time fields
            : getSuggestions(
                state.tables[index]?.id,
                tableTabularData.title,
                tableTabularData.value
              )
        }
      />
    );
  };

  // ====DOCUMENT======================DOCUMENT=========================DOCUMENT========================

  const docPayload = {
    generateType: "",
    blackAndWhite: "",
    orientation: "",
  };
  const [savedDocumentConfig, setSavedDocumentConfig] = useState(docPayload);

  const [openDocumentConfigPopup, setOpenDocumentConfigPopup] = useState(false);
  const [loadingDownloadDocument, setLoadingDownloadDocument] = useState(false);

  const openExportDocumentModal = () => setOpenDocumentConfigPopup(true);
  const closeExportDocumentModal = () => setOpenDocumentConfigPopup(false);

  const finishExportDocument = () => {
    ExportToDocument();
  };

  const navigate = useNavigate();

  // const formattedDate = moment().format('MM.DD.YYYY');

  const ExportToDocument = (type) => {
    setLoadingDownloadDocument(true);
    let data = JSON.stringify({
      reportId: state.reportData?.id,
      generateType: savedDocumentConfig?.generateType,
      orientation: savedDocumentConfig?.orientation,
      blackAndWhite:
        savedDocumentConfig?.blackAndWhite === "color" ? "false" : "true",
    });

    let config = {
      method: "post",
      maxBodyLength: Infinity,
      responseType: "arraybuffer",
      url: `${API_URL}/api/pdf/generate`,
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        console.log(JSON.stringify(response.data));
        setLoadingDownloadDocument(false);
        closeExportDocumentModal();
        setSavedDocumentConfig(docPayload);
        if (savedDocumentConfig?.generateType === "pdf") {
          const blob = new Blob([response.data], {
            type: "application/pdf",
          });

          // Create a temporary URL for the blob
          const url = window.URL.createObjectURL(blob);

          // Create a temporary link element to trigger the download
          const link = document.createElement("a");
          link.href = url;
          const safeReportName = (state.reportData.template || "Report")
            .replace(/[\\/:*?"<>|]/g, "-")
            .trim();
          link.download = `${safeReportName}-${DocumentExportFormatDate(
            new Date()
          )}.pdf`; // Set the file name
          document.body.appendChild(link);
          link.click();

          // Clean up: remove the link and revoke the URL
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else if (savedDocumentConfig?.generateType === "excel") {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          // Create a temporary URL for the blob
          const url = window.URL.createObjectURL(blob);

          // Create a temporary link element to trigger the download
          const link = document.createElement("a");
          link.href = url;
          const safeReportName = (state.reportData.template || "Report")
            .replace(/[\\/:*?"<>|]/g, "-")
            .trim();
          link.download = `${safeReportName}-${DocumentExportFormatDate(
            new Date()
          )}.xlsx`; // Set the file name
          document.body.appendChild(link);
          link.click();

          // Clean up: remove the link and revoke the URL
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoadingDownloadDocument(false);
      });
  };
  // console.log("savedDocumentConfig", savedDocumentConfig);

  const DeleteHandler = async () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsGlobalLoading(true);
      // setGlobalLoadingText("Deleting report...");
      
      if (state.reportData?.id) {
        const response = await DeleteSecurityReportAPI(state.reportData.id);
        console.log("DeleteHandler", response);
        if (response?.success) {
          showAlert("Report deleted successfully!", "success");
          navigate("/report");
        } else {
          showError(response?.message || "Failed to delete report.");
          console.error("Failed to delete report:", response?.message);
        }
      }
    } catch (error) {
      console.error("Error deleting report:", error);
      showError("An error occurred while deleting the report.");
    } finally {
      setIsGlobalLoading(false);
      setShowDeleteModal(false);
    }
  };

  // Send on GMAIL pdf

  const [openGmailConfigPopup, setOpenGmailConfigPopup] = useState(false);
  const [loadingGmailSendingDocument, setLoadingGmailSendingDocument] =
    useState(false);

  // Email form state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState({
    subject: "",
    text: "",
    to: state?.reportData?.securityEmail ? [state?.reportData?.securityEmail] : [],
    cc: [],
    bcc: [],
  });

  // console.log("securityEmail", state?.reportData?.securityEmail, emailForm);

  const openGmailSendingDocumentModal = () => {
    // Populate email form with data from response
    const emailData = state?.reportData?.email || {};
    setEmailForm((prev) => ({
      ...prev,
      to: emailData.to && Array.isArray(emailData.to) && emailData.to.length > 0
        ? emailData.to
        : state?.reportData?.securityEmail
        ? [state?.reportData?.securityEmail]
        : [],
      cc: emailData.cc && Array.isArray(emailData.cc) ? emailData.cc : [],
      bcc: emailData.bcc && Array.isArray(emailData.bcc) ? emailData.bcc : [],
    }));
    setShowEmailForm(true);
  };
  const closeGmailSendingDocumentModal = () => setShowEmailForm(false);

  // Handle email sending
  const handleSendEmail = async () => {
    setLoadingGmailSendingDocument(true);
    setIsGlobalLoading(true);
    // setGlobalLoadingText("Sending email...");

    try {
      // Ensure to, cc, and bcc are arrays
      const toEmails = Array.isArray(emailForm.to) 
        ? emailForm.to 
        : emailForm.to 
        ? [emailForm.to] 
        : state?.reportData?.securityEmail 
        ? [state?.reportData?.securityEmail] 
        : [];
      
      const ccEmails = Array.isArray(emailForm.cc) ? emailForm.cc : [];
      const bccEmails = Array.isArray(emailForm.bcc) ? emailForm.bcc : [];

      // Get exportConfig from response, with fallbacks to default values
      const exportConfig = state?.reportData?.exportConfig || {};
      const generateType = exportConfig.generateType || "pdf";
      const orientation = exportConfig.orientation || "portrait";
      // Convert boolean to string: false = color ("false"), true = black and white ("true")
      const blackAndWhite = exportConfig.blackAndWhite !== undefined
        ? String(exportConfig.blackAndWhite)
        : "false";

      const data = JSON.stringify({
        reportIds: [reportId],
        generateType: generateType,
        orientation: orientation,
        blackAndWhite: blackAndWhite,
        subject: emailForm.subject,
        text: emailForm.text,
        to: toEmails,
        cc: ccEmails,
        bcc: bccEmails,
        filename: emailForm.subject, // Use subject as filename
      });

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        responseType: "arraybuffer",
        url: `${API_URL}/api/pdf/sendreport`,
        headers: {
          "Content-Type": "application/json",
        },
        data,
      };

      await axios.request(config);

      // Reset form and close modal on success
      const emailData = state?.reportData?.email || {};
      setEmailForm({
        subject: "",
        text: "",
        to: emailData.to && Array.isArray(emailData.to) && emailData.to.length > 0
          ? emailData.to
          : state?.reportData?.securityEmail
          ? [state?.reportData?.securityEmail]
          : [],
        cc: emailData.cc && Array.isArray(emailData.cc) ? emailData.cc : [],
        bcc: emailData.bcc && Array.isArray(emailData.bcc) ? emailData.bcc : [],
      });
      setShowEmailForm(false);

      showAlert("Email sent successfully!", "success");
      console.log("Email sent successfully");
    } catch (error) {
      console.error("Email send error:", error);
      showError("Failed to send email. Please try again.");
    } finally {
      setLoadingGmailSendingDocument(false);
      setIsGlobalLoading(false);
    }
  };

  // Render logic
  return (
    <div className={`w-full text-sm h-full ${PixelStyle}`}>
      {/* Global Top Screen Loader */}
      <TopScreenLoader 
        isVisible={isGlobalLoading} 
        text={globalLoadingText}
        showText={true}
      />
      
      {/* <HeaderContainer pageTitle="Reporting">
        <span className="text-black">View Report</span>
      </HeaderContainer> */}
       <div className="flex justify-between items-center my-6">
          <div>
            <h1 className="text-2xl font-bold text-[#004175]">View Report</h1>
          
          </div>
          <div>

          <Button
            variant="outline"
            onClick={() => navigate("/report")}
            className="text-gray-600 border-gray-600 hover:bg-gray-50"
            >
            Back to Reports
          </Button>
            </div>
        </div>
      <div className="w-full text-sm h-full">
        {state.wantToSubmitToggle && (
          <div className="fixed z-50 inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
            <div className="bg-white shadow-lg rounded-lg p-6 w-1/3">
              <h2 className="text-lg font-semibold text-center mb-4">
                Want to Submit
              </h2>
              <div className="flex justify-around">
                <button
                  className="bg-green-500 text-white px-4 py-2 rounded"
                  onClick={SubmitUpdateHandler}
                >
                  Yes
                </button>
                <button
                  className="bg-red-500 text-white px-4 py-2 rounded"
                  onClick={() =>
                    dispatch({ type: "TOGGLE_SUBMIT", payload: false })
                  }
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {state.loading ? (
          <div className="text-center py-10">Loading...</div>
        ) : state.message ? (
          <div className="text-center py-10">{state.message}</div>
        ) : (
          <div className="border bg-white rounded-2xl w-full ">
            <div className="flex justify-between items-start w-full px-5 py-2">
              <div>
                <div>
                  <span className="font-semibold">Company Name:</span>{" "}
                  {state.companyName}
                </div>
                <div>
                  <span className="font-semibold">Location: </span>{" "}
                  {state.companyLocation}
                </div>
                <div>
                  <span className="font-semibold">Report Name:</span>{" "}
                  {state.reportName}
                </div>
              </div>
              <div className="flex gap-2 items-center mt-2">
                {/* Buttons */}
                {/* <ReportButton>Repopulate</ReportButton> */}
                <ReportButton
                  // className="bg-blue-500 text-white px-4 py-2 rounded"
                  onClick={DeleteHandler}
                >
                  <Trash size={14} />
                </ReportButton>

                <div className="flex">
                  {!state.isEditable ? (
                    <ReportButton
                      // className="bg-blue-500 text-white px-4 py-2 rounded"
                      onClick={() =>
                        dispatch({ type: "TOGGLE_EDITABLE", payload: true })
                      }
                    >
                      <SquarePen size={14} />
                    </ReportButton>
                  ) : (
                    <div className="flex gap-2">
                      <ReportButton
                        // className="bg-green-600 text-white px-4 py-2 rounded"
                        onClick={() => handleSave("data")}
                      >
                        Save
                      </ReportButton>
                      <ReportButton
                        // className="bg-red-600 text-white px-4 py-2 rounded"
                        onClick={handleDocumentCancel}
                      >
                        Cancel
                      </ReportButton>
                    </div>
                  )}
                </div>
                <ReportButton
                  onClick={openExportDocumentModal}
                  className="flex  gap-1 items-center cursor-pointer"
                >
                  {/* <div onClick={() => ExportToDocument("pdf")}> */}
                  {/* <FolderUp size={16} /> */}
                  {/* <span>Export</span>{" "} */}
                  <FileUp size={14} />
                </ReportButton>
                <ReportButton
                  onClick={openGmailSendingDocumentModal}
                  className="flex  items-center  cursor-pointer"
                >
                  {/* <div onClick={() => ExportToDocument("pdf")}> */}
                  {/* <FolderUp size={16} /> */}
                  <span>
                    <SiGmail size={14} />
                  </span>
                </ReportButton>

                {state.reportData?.isSubmittedByManagement ? (
                  <div className="text-green-600 text-xs font-semibold">Submitted</div>
                ) : (
                  <ReportButton
                    // className="bg-blue-500 text-white px-4 py-2 rounded-md"
                    onClick={() =>
                      dispatch({ type: "TOGGLE_SUBMIT", payload: true })
                    }
                  >
                    Submit
                  </ReportButton>
                )}
              </div>
            </div>
            <div className="grid gap-4 p-2 w-full">
              {(state.reportData?.filledData || []).map((item, index) => (
                <div
                  key={index}
                  className="border rounded-lg shadow-md p-4 bg-white relative overflow-auto"
                >
                  {state.tables[index]?.type === "Document" &&
                  state.tableDataByIndex[index]?.length > 0 ? (

                      (() => {
                        const columns = state.tables[index]?.columns || [];
                        // Map columns with their original index to preserve mapping to data
                        const columnWithIndex = columns.map((col, i) => ({
                          ...col,
                          originalIndex: i,
                        }));

                        // Sort by sequence (stable: originalIndex tiebreaker when equal)
                        const sortedColumns = [...columnWithIndex].sort((a, b) => {
                          const seqA = !isNaN(parseInt(a.sequence, 10)) ? parseInt(a.sequence, 10) : Infinity;
                          const seqB = !isNaN(parseInt(b.sequence, 10)) ? parseInt(b.sequence, 10) : Infinity;
                          const diff = seqA - seqB;
                          return diff !== 0 ? diff : a.originalIndex - b.originalIndex;
                        });

                        return (
                          <div className="flex flex-wrap w-full">
                            {sortedColumns.map((column, colIndex) => {
                              const tableDocumentData = state.tableDataByIndex[index][0].row[column.originalIndex];
                              if (!tableDocumentData) return null;

                              const width = column.width ? `${column.width}%` : "50%";
                              
                              return (
                                <div
                                  key={colIndex}
                                  className="p-2"
                                  style={{ width: width }}
                                >
                                  {renderDocumentPreview(
                                    tableDocumentData,
                                    column.originalIndex,
                                    [column], // Pass the column definition as filterTablebySequence
                                    index
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()
                  ) : state.tables[index]?.type === "Tabular" ? (
                    <div className="w-full relative">
                      <div className="flex justify-between mb-5">
                        <p className="font-semibold capitalize text-sm">
                          {state.tables[index]?.tableName || ""}
                        </p>
                        {state.isEditable &&
                          !state.tables[index]?.columns?.some(
                            (col) => col.format === "fixed value"
                          ) && (
                            <ReportButton
                              className=""
                              onClick={() => handleAddRow(index)}
                            >
                              Add Row
                            </ReportButton>
                          )}
                      </div>
                      <div className="w-full overflow-auto">
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => handleDragEnd(event, index)}
                        >
                          {(() => {
                            const columns = state.tables[index]?.columns || [];
                            // Map columns with their original index
                            const columnWithIndex = columns.map((col, i) => ({
                              ...col,
                              originalIndex: i,
                            }));

                            // Sort by sequence (stable: originalIndex tiebreaker when equal)
                            const sortedColumns = [...columnWithIndex].sort((a, b) => {
                              const seqA = !isNaN(parseInt(a.sequence, 10)) ? parseInt(a.sequence, 10) : Infinity;
                              const seqB = !isNaN(parseInt(b.sequence, 10)) ? parseInt(b.sequence, 10) : Infinity;
                              const diff = seqA - seqB;
                              return diff !== 0 ? diff : a.originalIndex - b.originalIndex;
                            });

                            const colCount = sortedColumns.length;
                            const evenWidth = colCount > 0 ? 100 / colCount : 100;

                            return (
                              <table className="w-full text-sm text-left border border-zinc-300">
                                <thead className="text-sm text-center text-gray-700 capitalize bg-[#004278] text-white border border-zinc-300">
                                  <tr>
                                    {sortedColumns.map((column, colIndex) => (
                                      <th
                                        scope="col"
                                        className="px-6 py-3"
                                        key={colIndex}
                                        style={{ width: `${evenWidth}%` }}
                                      >
                                        {column?.title || ""}
                                      </th>
                                    ))}
                                    <th
                                      className="px-6 py-3 "
                                      style={{ width: "150px" }}
                                    >
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <SortableContext
                                  items={(state.tableDataByIndex[index] || []).map(
                                    (row) =>
                                      row.id || `fallback-${index}-${Math.random()}`
                                  )}
                                  strategy={verticalListSortingStrategy}
                                >
                                  <tbody>
                                    {(state.tableDataByIndex[index] || []).map(
                                      (content, contentIndex) => (
                                        <SortableRow
                                          key={
                                            content.id ||
                                            `key-${index}-${contentIndex}`
                                          }
                                          content={content}
                                          contentIndex={contentIndex}
                                          index={index}
                                          state={state}
                                          renderTableCell={renderTableCell}
                                          DeleteTabularHandler={
                                            DeleteTabularHandler
                                          }
                                          MomentFormatDate={MomentFormatDate}
                                          sortedColumns={sortedColumns}
                                        />
                                      )
                                    )}
                                  </tbody>
                                </SortableContext>
                              </table>
                            );
                          })()}
                        </DndContext>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Card popup model */}
      {openDocumentConfigPopup && (
        <CardPopModel
          isOpen={openExportDocumentModal}
          onClose={closeExportDocumentModal}
          title="Configure Export"
          size="md"
        >
          <DocumentConfigurator
            savedDocumentConfig={savedDocumentConfig}
            setSavedDocumentConfig={setSavedDocumentConfig}
            finishExportDocument={finishExportDocument}
            loading={loadingDownloadDocument}
          />
        </CardPopModel>
      )}

      {/* Email Form Modal */}
      <EmailFormModal
        showEmailForm={showEmailForm}
        setShowEmailForm={setShowEmailForm}
        emailForm={emailForm}
        setEmailForm={setEmailForm}
        onSendEmail={handleSendEmail}
        loading={loadingGmailSendingDocument}
        defaultEmail={state?.reportData?.securityEmail}
        reportName={state.reportName}
        // currentDate={moment().format("MM/DD/YYYY")}
        currentDate={moment(state.reportData?.createdAt).format("MM/DD/YYYY")}
      />

      {/* Signature Saved Popup */}
      {state.showSignaturePopup && (
        <div className="fixed z-50 inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white shadow-lg rounded-lg p-6 w-80 text-center">
            <div className="mb-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              Signature Saved
            </h3>
            <p className="text-sm text-gray-500">
              Your signature has been successfully saved.
            </p>
            <div className="mt-4">
              <button
                onClick={() =>
                  dispatch({ type: "TOGGLE_SIGNATURE_POPUP", payload: false })
                }
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Report"
        message="Are you sure you want to delete this report? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Success Alert Modal */}
      <AlertModal
        isOpen={showSuccessAlert}
        onClose={() => setShowSuccessAlert(false)}
        title="Success"
        message={alertMessage}
        type="success"
        buttonText="OK"
      />

      {/* Error Alert Modal */}
      <AlertModal
        isOpen={showErrorAlert}
        onClose={() => setShowErrorAlert(false)}
        title="Error"
        message={alertMessage}
        type="error"
        buttonText="OK"
      />
    </div>
  );
};

const MomentFormatDate = (date) => {
  if (date == null || date === "") return "";
  const s = String(date).trim();
  if (/^\d+$/.test(s)) {
    return moment(parseInt(s, 10)).format("YYYY-MM-DD HH:mm");
  }
  const parsed = moment(
    s,
    [
      "MM/DD/YYYY hh:mmA",
      "MM/DD/YYYY h:mmA",
      "MM/DD/YYYY hh:mm A",
      "MM/DD/YYYY h:mm A",
    ],
    true
  );
  if (parsed.isValid()) return parsed.format("YYYY-MM-DD HH:mm");
  const fallback = moment(s);
  return fallback.isValid() ? fallback.format("YYYY-MM-DD HH:mm") : s;
};

export default ViewManagementReport;

export const DocumentExportFormatDate = (date) => {
  return moment(date).format("MM.DD.YYYY");

  // return moment(parseInt(date)).format("MM.DD.YYYY");
};

export const DocumentConfig = {
  generateType: [
    { id: 1, title: "pdf" },
    { id: 2, title: "excel" }, // Fixed duplicate ID
  ],
  blackAndWhite: [
    { id: 1, title: "color" },
    { id: 2, title: "black & White" },
  ],
  orientation: [
    { id: 1, title: "portrait" },
    { id: 2, title: "landscape" },
  ],
};
// DocumentConfigurator

export const DocumentConfigurator = ({
  savedDocumentConfig,
  setSavedDocumentConfig,
  finishExportDocument,
  loading,
}) => {

  const handleSelection = (stepKey, title) => {
    setSavedDocumentConfig((prev) => {
      const updated = {
        ...prev,
        [stepKey]: title,
      };
      return updated;
    });
  };

  const isStepComplete = (stepKey) => {
    return savedDocumentConfig[stepKey] !== "" && savedDocumentConfig[stepKey] !== undefined;
  };

  // Check if download button should be enabled - use useMemo for better reactivity
  const isDownloadEnabled = React.useMemo(() => {
    if (loading) return false;
    if (!isStepComplete("generateType")) return false;
    // For Excel, only need generateType
    if (savedDocumentConfig.generateType === "excel") return true;
    // For PDF, need all fields
    if (savedDocumentConfig.generateType === "pdf") {
      return isStepComplete("blackAndWhite") && isStepComplete("orientation");
    }
    return false;
  }, [loading, savedDocumentConfig.generateType, savedDocumentConfig.blackAndWhite, savedDocumentConfig.orientation]);

  return (
    <>
      <div className="p-6 space-y-6">
        {/* File Format */}
        <div className="space-y-3">
          <div className="flex gap-3">
            <button
              onClick={() => handleSelection("generateType", "pdf")}
              className={`py-3 px-6 rounded-md font-medium transition-colors w-full ${
                savedDocumentConfig.generateType === "pdf"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              PDF
            </button>
            <button
              onClick={() => handleSelection("generateType", "excel")}
              className={`py-3 px-6 rounded-md font-medium transition-colors w-full ${
                savedDocumentConfig.generateType === "excel"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Excel
            </button>
          </div>
        </div>

        {/* Color Settings */}
        {savedDocumentConfig.generateType === "pdf" && (
          <div className="space-y-3">
            <div className="flex gap-3">
              <button
                onClick={() => handleSelection("blackAndWhite", "blackAndWhite")}
                className={`py-3 px-6 rounded-md font-medium transition-colors w-full ${
                  savedDocumentConfig.blackAndWhite === "blackAndWhite"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Black & White
              </button>
              <button
                onClick={() => handleSelection("blackAndWhite", "color")}
                className={`py-3 px-6 rounded-md font-medium transition-colors w-full ${
                  savedDocumentConfig.blackAndWhite === "color"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Color
              </button>
            </div>
          </div>
        )}

        {/* Page Orientation */}
        {savedDocumentConfig.generateType === "pdf" && (
          <div className="space-y-3">
            <div className="flex gap-3">
              <button
                onClick={() => handleSelection("orientation", "landscape")}
                className={`py-3 px-6 rounded-md font-medium transition-colors w-full ${
                  savedDocumentConfig.orientation === "landscape"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Landscape
              </button>
              <button
                onClick={() => handleSelection("orientation", "portrait")}
                className={`py-3 px-6 rounded-md font-medium transition-colors w-full ${
                  savedDocumentConfig.orientation === "portrait"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Portrait
              </button>
            </div>
          </div>
        )}

        {/* Download Button */}
        <div className="flex justify-center pt-4 border-t">
          <button
            onClick={() => {
              finishExportDocument();
            }}
            disabled={!isDownloadEnabled}
            className="px-8 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <LoaderCircle className="animate-spin w-5 h-5" />
                Downloading...
              </div>
            ) : (
              "Download"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

const ReportButton = ({ children, className, ...props }) => {
  return (
    <button
    style={{border: "1px solid #004278"}}
      className={` border border-[#004278] text-xs text-[#004278]  font-semibold px-4 py-1.5 rounded ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};
